import React, { useEffect, useState } from 'react';
import api from '../../api';

const Spinner = ({ size = 4, className = "" }) => (
  <svg className={`animate-spin text-white ${className}`} style={{ width: `${size * 0.25}rem`, height: `${size * 0.25}rem` }} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
  </svg>
);

const Message = ({ type, children, onDismiss }) => {
  const bgColor = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800'
  }[type];

  const icon = {
    success: (
      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
      </svg>
    ),
    error: (
      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    ),
    info: (
      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
      </svg>
    )
  }[type];

  return (
    <div className={`flex items-start p-4 mb-4 border rounded-lg text-sm ${bgColor}`}>
      <span className="mr-3 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1 break-words">{children}</div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="ml-3 text-gray-500 hover:text-gray-700 focus:outline-none p-1"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      )}
    </div>
  );
};

const HODCard = ({ hod, onRemove, loading }) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-blue-50 border border-blue-100 rounded-xl gap-4">
      <div className="flex items-start sm:items-center">
        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
          </svg>
        </div>
        <div className="ml-3 break-all">
          <h3 className="text-sm font-semibold text-gray-900">{hod.name}</h3>
          <p className="text-xs text-gray-600 mt-0.5">{hod.email}</p>
          {hod.department && (
            <p className="text-xs text-blue-700 bg-blue-100/50 inline-block px-2 py-0.5 rounded mt-1.5 font-medium">
              Also manages: {hod.department}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onRemove}
        disabled={loading}
        className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 sm:py-1 border border-transparent text-xs font-semibold rounded-lg text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition"
      >
        {loading ? <Spinner size={3} className="text-red-700" /> : 'Remove'}
      </button>
    </div>
  );
};

const HODManager = ({ 
  tenantId, 
  departmentId, 
  onHODChanged, 
  departmentName,
  onRefreshNeeded // NEW: Callback to refresh parent data
}) => {
  const [hods, setHODs] = useState([]);
  const [currentHOD, setCurrentHOD] = useState(null);
  const [selectedHOD, setSelectedHOD] = useState('');
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const refreshAllData = React.useCallback(async () => {
    if (!tenantId || !departmentId) return;
    setLoadingFetch(true);
    setMessage('');
    try {
      const [hodsRes, deptRes, allDeptsRes] = await Promise.all([
        api.get(`/api/hods/${tenantId}`),
        api.get(`/api/departments/${tenantId}/${departmentId}`),
        api.get(`/api/departments/${tenantId}`)
      ]);
      const departments = allDeptsRes.data.departments || [];
      const enhancedHODs = (hodsRes.data.hods || []).map(hod => {
        const managedDept = departments.find(
          d => d.hodId === (hod._id || hod.id || hod.userId)
        );
        return {
          ...hod,
          department: managedDept ? managedDept.name : null
        };
      });
      setHODs(enhancedHODs);
      setCurrentHOD(deptRes.data.department?.hodId || null);
      
      // NEW: Trigger parent refresh if callback provided
      if (onRefreshNeeded) {
        onRefreshNeeded();
      }
    } catch (err) {
      setMessage('Error fetching data');
      setMessageType('error');
    } finally {
      setLoadingFetch(false);
    }
  }, [tenantId, departmentId, onRefreshNeeded]);

  const assignHOD = async () => {
    if (!selectedHOD) {
      setMessage('Please select a HOD.');
      setMessageType('error');
      return;
    }
    setLoadingAssign(true);
    setMessage('');
    try {
      await api.put(`/api/assign-hod/${departmentId}`, {
        hodId: selectedHOD,
        tenantId,
      });
      await refreshAllData();
      setMessage('HOD assigned successfully.');
      setMessageType('success');
      setSelectedHOD('');
      if (onHODChanged) onHODChanged(selectedHOD);
      
      // NEW: Force refresh parent data
      if (onRefreshNeeded) {
        setTimeout(() => onRefreshNeeded(), 100);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error assigning HOD.';
      setMessage(errorMsg);
      setMessageType('error');
    } finally {
      setLoadingAssign(false);
    }
  };

  const confirmRemoveHOD = () => {
    setShowConfirmation(true);
  };

  const removeHOD = async () => {
    setLoadingRemove(true);
    setMessage('');
    setShowConfirmation(false);
    try {
      await api.put(`/api/remove-or-change-hod/${departmentId}`, {
        tenantId,
        hodId: null,
      });
      await refreshAllData();
      setMessage('HOD removed successfully.');
      setMessageType('success');
      setSelectedHOD('');
      if (onHODChanged) onHODChanged(null);
      
      // NEW: Force refresh parent data
      if (onRefreshNeeded) {
        setTimeout(() => onRefreshNeeded(), 100);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error removing HOD.';
      setMessage(errorMsg);
      setMessageType('error');
    } finally {
      setLoadingRemove(false);
    }
  };

  const cancelRemove = () => {
    setShowConfirmation(false);
  };

  useEffect(() => {
    refreshAllData();
  }, [tenantId, departmentId, refreshAllData]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const filteredHODs = hods.filter(hod =>
    (hod.name && hod.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (hod.email && hod.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const currentHODObj = hods.find(
    h => h._id === currentHOD || h.id === currentHOD || h.userId === currentHOD
  );

  const dismissMessage = () => {
    setMessage('');
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6 border-b border-gray-100 pb-4">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
          </svg>
          <h2 className="text-base sm:text-lg font-bold text-gray-900">Head of Department</h2>
        </div>
        {departmentName && (
          <span className="self-start sm:self-auto px-3 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">
            {departmentName}
          </span>
        )}
      </div>

      {message && (
        <Message type={messageType} onDismiss={dismissMessage}>
          {message}
        </Message>
      )}

      {loadingFetch ? (
        <div className="flex flex-col items-center justify-center py-10 text-gray-500 gap-2">
          <Spinner size={6} className="text-blue-600" />
          <span className="text-sm font-medium">Loading HOD data...</span>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-3">Current HOD:</h3>
            {currentHODObj ? (
              <HODCard
                hod={currentHODObj}
                onRemove={confirmRemoveHOD}
                loading={loadingRemove}
              />
            ) : (
              <div className="p-6 bg-gray-50 border border-gray-200 border-dashed rounded-xl text-center">
                <p className="text-sm font-medium text-gray-500">No HOD assigned to this department</p>
                <p className="text-xs text-gray-400 mt-1">
                  Assign a HOD to manage this department
                </p>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-4">Assign New HOD:</h3>

            <div className="mb-4">
              <label htmlFor="search-hod" className="block text-xs font-semibold text-gray-600 mb-1.5">
                Search HODs
              </label>
              <input
                id="search-hod"
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none outline-none"
              />
            </div>

            {!currentHODObj && (
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="w-full relative">
                  <select
                    id="hod-select"
                    value={selectedHOD}
                    onChange={e => setSelectedHOD(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition outline-none bg-white target:bg-white"
                    disabled={loadingAssign || loadingRemove || loadingFetch}
                  >
                    <option value="">Select a HOD</option>
                    {filteredHODs.map(h => {
                      const hodId = h._id || h.id || h.userId;
                      return (
                        <option key={hodId} value={hodId}>
                          {h.name} ({h.email}){h.department && ` - Currently manages ${h.department}`}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={assignHOD}
                  className="w-full lg:w-auto inline-flex items-center justify-center px-5 py-3 sm:py-2 rounded-lg font-semibold text-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition disabled:opacity-60 whitespace-nowrap"
                  disabled={loadingAssign || loadingRemove || loadingFetch || !selectedHOD}
                >
                  {loadingAssign && <Spinner className="mr-2" />}
                  Assign HOD
                </button>
              </div>
            )}

            {currentHODObj && (
              <button
                type="button"
                onClick={confirmRemoveHOD}
                className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-3 sm:py-2 rounded-lg font-semibold text-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition disabled:opacity-60 mt-2"
                disabled={loadingRemove || loadingAssign || loadingFetch}
              >
                {loadingRemove && <Spinner className="mr-2" />}
                Remove HOD
              </button>
            )}
          </div>
        </>
      )}

      {showConfirmation && (
        <div className="fixed inset-0 bg-gray-900/60 flex items-end sm:items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-6 shadow-xl transform transition-all">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Confirm Removal</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to remove {currentHODObj?.name} as HOD of this department?
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:space-x-3">
              <button
                type="button"
                onClick={cancelRemove}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={removeHOD}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
              >
                Remove HOD
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HODManager;